import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import { PersonaFisica as PersonaFisicaBuscador } from '@conacyt/buscador-cvu';
import { Persona } from '@/shared/model/persona/persona.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import { CharCodes } from '@/shared/model/enumerations/caracteres.model';
import { BTable } from 'bootstrap-vue';
import { Solucion } from '@/shared/model/solucion.model';
import { IRubro } from '@/shared/model/rubro.model';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import { mixins } from 'vue-class-component';
import ApeironComponent from '@/components/commons/apeiron-component.model';

@Component({
  computed: {
    userInfo: {
      get() {
        return this.$store.getters.userInfo;
      },
    },
  },
})
export default class DatosGeneralesComponent extends mixins(ApeironComponent) {
  public personaCvu: PersonaFisicaBuscador = null;

  @Prop({ default: false })
  public solicitud: Solicitud;

  @Prop({ required: true })
  public solucion: Solucion;

  @Ref('tableRubros')
  public tableRubros: BTable;

  public persona = new Persona();

  public valueValidateInput;
  public valueFormatInput;

  public parts;
  public integerPart;
  public decimalPart;
  public issste = false;
  public readonly userInfo: any;

  public fields = [{ label: 'Rubros', key: 'rubros' }];

  /**
   * Carga inicial
   */
  mounted() {
    if (this.solicitud?.beneficiario != null) {
      this.persona = this.solicitud.beneficiario;
      this.issste = this.solucion.params.tipoBecaExtranjero === 'CONVENIO_ISSSTE';
      this.fieldsRubros();
      this.tableRubros.refresh();
    }
  }

  private fieldsRubros() {
    // Encuentra el número máximo de propiedades "anio_X" en montos
    let maxAnios = 0;
    this.solicitud.datos_conciliados.rubros.forEach((rubro: IRubro) => {
      const numAnios = Object.keys(rubro.montos).length;
      if (numAnios > maxAnios) {
        maxAnios = numAnios;
      }
      //Redondea monto al cargar informacion
      for (const key in rubro.montos) {
        if (rubro.montos.hasOwnProperty(key)) {
          // Redondear el monto sin decimales
          const montoOriginal = rubro.montos[key].monto;
          const montoRedondeado = Math.round(Number(montoOriginal));
          rubro.montos[key].monto = montoRedondeado;
        }
      }
    });

    // Agregar campos a fields basados en el número máximo de años si no existen
    for (let i = 1; i <= maxAnios; i++) {
      const key = `anio_${i}`;
      const label = `${i}º año`;

      // Verificar si el campo ya existe en fields
      const exists = this.fields.some(field => field.key === key);

      if (!exists) {
        this.fields.push({
          label: label,
          key: key,
        });
      }
    }

    // Eliminar campos que ya no son necesarios
    this.fields = this.fields.filter(field => {
      if (field.key && field.key.startsWith('anio_')) {
        const anioNumero = parseInt(field.key.split('_')[1], 10);
        return anioNumero <= maxAnios;
      }
      return true; // Mantener los campos que no empiezan con 'anio_'
    });
  }

  get isValidador(): boolean {
    return this.solucion.autoridades.some(
      user => user.usuarioId == this.userInfo.login && user.roles.some(rol => rol === RolAutoridad.VALIDADOR_DOCUMENTAL)
    );
  }

  public formatInput(item: any, key: string) {
    this.valueFormatInput = item[key];

    // Verificar si el valor es un número y no está vacío
    if (typeof this.valueFormatInput === 'number' || (typeof this.valueFormatInput === 'string' && !isNaN(Number(this.valueFormatInput)))) {
      this.valueFormatInput = String(this.valueFormatInput); // Convertir a cadena si es número

      // Verificar si la cadena contiene un punto decimal
      if (this.valueFormatInput.includes('.')) {
        this.parts = this.valueFormatInput.split('.');
        this.integerPart = this.parts[0];
        this.decimalPart = this.parts[1] ? this.parts[1].substring(0, 2) : '';
        item[key] = this.decimalPart ? `${this.integerPart}.${this.decimalPart}` : this.integerPart;
      } else {
        // Si no contiene un punto decimal, no hacer nada
        this.integerPart = this.valueFormatInput;
        this.decimalPart = '';
        item[key] = this.integerPart;
      }
    }
  }

  public validateInput(item: any, key: string) {
    this.valueValidateInput = item[key];
    // Reemplazar cualquier carácter que no sea un dígito o un punto
    this.valueValidateInput = this.valueValidateInput.replace(/[^0-9.]/g, '');
    // Asegúrese de que solo haya un punto presente
    if (this.valueValidateInput.split('.').length > 2) {
      this.valueValidateInput = this.valueValidateInput.replace(/\.+$/, '');
    }
    item[key] = this.valueValidateInput;
  }

  public numbersOnly(evt: any) {
    const charCode = evt.which ? evt.which : evt.keyCode;
    // Bloquea el ingreso del número 0 como único dígito y cualquier carácter que no sea un número ni el punto decimal
    if (charCode === CharCodes.CERO && evt.target.value.length === 0) {
      evt.preventDefault();
    } else if ((charCode < CharCodes.CERO || charCode > CharCodes.NUEVE) && charCode !== CharCodes.DELETE) {
      evt.preventDefault();
    } else {
      return true;
    }
  }

  /**
   * Obtiene programas de los parametos
   */
  public getNombreRubro(rubro: string): string {
    switch (rubro) {
      case 'MANUTENCION':
        if (this.issste) {
          return 'Apoyo económico';
        }
        return this.solucion?.params?.manutencion ?? 'MANUTENCION';
        break;
      case 'COLEGIATURA':
        if (this.issste) {
          return 'Apoyo complementario para formación';
        }
        return this.solucion?.params?.colegiatura ?? 'COLEGIATURA';
        break;
      case 'SEGURO_MEDICO':
        if (this.issste) {
          return 'Apoyo para gastos de seguro médico';
        }
        return this.solucion?.params?.seguroMedico ?? 'SEGURO_MEDICO';
        break;
      case 'BOLETO_AVION':
        return 'Apoyo complementario para traslado';
        break;
      default:
        return rubro;
        break;
    }
  }
}
